import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamesAPI, roundsAPI, teamsAPI, refereesAPI, stadiaAPI, projectsAPI } from '../../services/api';
import GameEvents from './GameEvents';
import GameRoster from './GameRoster';
import GameTicker from './GameTicker';

const RoundGames = () => {
  const { projectId, roundId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [round, setRound] = useState(null);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [referees, setReferees] = useState([]);
  const [stadia, setStadia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [eventsGame, setEventsGame] = useState(null);
  const [rosterGame, setRosterGame] = useState(null);
  const [tickerGame, setTickerGame] = useState(null);
  const [selectedHomeTeam, setSelectedHomeTeam] = useState(null);
  const [selectedAwayTeam, setSelectedAwayTeam] = useState(null);
  const [inlineEditGame, setInlineEditGame] = useState(null);
  const [inlineEditData, setInlineEditData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    hid: '',
    aid: '',
    refid: '',
    stid: '',
    startdate: '',
    starttime: '',
    scoreh: '',
    scorea: '',
    played: false
  });
  useEffect(() => {
    fetchProject();
    fetchRound();
    fetchGames();
    fetchTeams();
    fetchReferees();
    fetchStadia();
  }, [roundId]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      setProject(response.data);
    } catch (err) {
      setError('Failed to fetch project');
    }
  };

  const fetchRound = async () => {
    try {
      const response = await roundsAPI.getById(roundId);
      setRound(response.data);
    } catch (err) {
      setError('Failed to fetch round');
    }
  };
  const fetchGames = async () => {
    try {
      const response = await gamesAPI.getByRound(roundId);
      // Handle the paginated response structure
      const gamesData = response.data.games || response.data || [];
      setGames(gamesData);
    } catch (err) {
      setError('Failed to fetch games');
    } finally {
      setLoading(false);
    }
  };
  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getByProject(projectId);
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams for project:', err);
      // Fallback to all teams if project-specific endpoint fails
      try {
        const allTeamsResponse = await teamsAPI.getAll();
        setTeams(allTeamsResponse.data);
      } catch (fallbackErr) {
        console.error('Failed to fetch all teams as fallback:', fallbackErr);
      }
    }
  };
  const fetchReferees = async () => {
    try {
      const response = await refereesAPI.getAll();
      // Handle both array and paginated response
      const refereesData = response.data.referees || response.data || [];
      setReferees(refereesData);
    } catch (err) {
      console.error('Failed to fetch referees:', err);
    }
  };

  const fetchStadia = async () => {
    try {
      const response = await stadiaAPI.getAll();
      // Handle paginated response structure
      const stadiaData = response.data.stadia || response.data || [];
      setStadia(stadiaData);
    } catch (err) {
      console.error('Failed to fetch stadia:', err);
    }
  };

  const handleQuickCreateGame = async () => {
    if (!selectedHomeTeam || !selectedAwayTeam) return;
    
    try {
      const roundStartDate = round?.start ? round.start.split('T')[0] : '';
      
      const gameData = {
        pid: projectId,
        rid: roundId,
        hid: selectedHomeTeam._id,
        aid: selectedAwayTeam._id,
        refid: null,
        stid: null,
        startdate: roundStartDate || null,
        starttime: null,        scoreh: null, // Use null instead of 0 for unset scores
        scorea: null,
        played: 0
      };      await gamesAPI.create(gameData);
        // Reset selections but keep Quick Add open
      setSelectedHomeTeam(null);
      setSelectedAwayTeam(null);
      // Don't close Quick Add - keep it open for continuous use
      fetchGames();
      
      // Show success message briefly
      setError(''); // Clear any existing errors
      setSuccessMessage(`Game created: ${selectedHomeTeam.name} vs ${selectedAwayTeam.name}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error creating game:', err);
      setError(err.response?.data?.message || 'Failed to create game');
    }
  };  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Handle score values - convert empty strings to null for unset scores, or to numbers
      const homeScore = formData.scoreh === '' ? null : parseInt(formData.scoreh);
      const awayScore = formData.scorea === '' ? null : parseInt(formData.scorea);
      
      // Handle NaN cases by converting to null
      const finalHomeScore = isNaN(homeScore) ? null : homeScore;
      const finalAwayScore = isNaN(awayScore) ? null : awayScore;
      
      const submitData = {
        pid: projectId,
        rid: roundId,
        hid: formData.hid,
        aid: formData.aid,
        refid: formData.refid || null,
        stid: formData.stid || null,
        startdate: formData.startdate || null,
        starttime: formData.starttime || null,
        scoreh: finalHomeScore,
        scorea: finalAwayScore,
        played: formData.played ? 1 : 0
      };

      if (editingGame) {
        await gamesAPI.update(editingGame._id, submitData);
      } else {
        await gamesAPI.create(submitData);
      }
      
      fetchGames();
      resetForm();
    } catch (err) {
      console.error('Error saving game:', err);
      setError(err.response?.data?.message || 'Failed to save game');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this game?')) {
      try {
        await gamesAPI.delete(id);
        fetchGames();
      } catch (err) {
        setError('Failed to delete game');
      }
    }
  };
  const resetForm = () => {
    setShowModal(false);
    setEditingGame(null);
    // Auto-fill start date from round start date when creating new games
    const roundStartDate = round?.start ? round.start.split('T')[0] : '';
    setFormData({
      hid: '',
      aid: '',
      refid: '',
      stid: '',
      startdate: roundStartDate, // Auto-fill with round start date
      starttime: '',
      scoreh: '',
      scorea: '',
      played: false
    });
  };  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      hid: game.hid?._id || '',
      aid: game.aid?._id || '',
      refid: game.refid?._id || '',
      stid: game.stid?._id || '',
      startdate: game.startdate ? game.startdate.split('T')[0] : '',
      starttime: game.starttime || '',
      scoreh: game.scoreh !== null && game.scoreh !== undefined ? game.scoreh : '',
      scorea: game.scorea !== null && game.scorea !== undefined ? game.scorea : '',
      played: game.played === 1
    });
    setShowModal(true);
  };
  const handleInlineEdit = (game) => {
    setInlineEditGame(game._id);
    setInlineEditData({
      scoreh: game.scoreh !== null && game.scoreh !== undefined ? game.scoreh : '',
      scorea: game.scorea !== null && game.scorea !== undefined ? game.scorea : '',
      startdate: game.startdate ? game.startdate.split('T')[0] : '',
      starttime: game.starttime || '',
      played: game.played === 1
    });
  };  const handleInlineSave = async (gameId) => {
    try {
      const game = games.find(g => g._id === gameId);
      
      // Convert empty strings to 0, but preserve actual 0 values
      const homeScore = inlineEditData.scoreh === '' ? 0 : parseInt(inlineEditData.scoreh);
      const awayScore = inlineEditData.scorea === '' ? 0 : parseInt(inlineEditData.scorea);
      
      // Handle NaN cases by setting to 0
      const finalHomeScore = isNaN(homeScore) ? 0 : homeScore;
      const finalAwayScore = isNaN(awayScore) ? 0 : awayScore;
        // Auto-set played to true if any scores are entered (including 0:0)
      const hasScoresSet = inlineEditData.scoreh !== '' || inlineEditData.scorea !== '';
      const autoPlayed = hasScoresSet; // Mark as played whenever scores are entered
      
      const submitData = {
        pid: projectId,
        rid: roundId,
        hid: game.hid._id,
        aid: game.aid._id,
        refid: game.refid?._id || null,
        stid: game.stid?._id || null,
        startdate: inlineEditData.startdate || null,
        starttime: inlineEditData.starttime || null,
        scoreh: hasScoresSet ? finalHomeScore : null, // Only set scores if user entered something
        scorea: hasScoresSet ? finalAwayScore : null,
        played: autoPlayed ? 1 : 0 // Always set to played when scores are entered
      };

      await gamesAPI.update(gameId, submitData);
      setInlineEditGame(null);
      setInlineEditData({});
      fetchGames();
    } catch (err) {
      console.error('Error updating game:', err);
      setError('Failed to update game');    }
  };

  const startInlineEdit = (game) => {
    setInlineEditGame(game._id);
    setInlineEditData({
      startdate: game.startdate ? new Date(game.startdate).toISOString().split('T')[0] : '',
      starttime: game.starttime || '',
      scoreh: game.scoreh !== null && game.scoreh !== undefined ? game.scoreh.toString() : '',
      scorea: game.scorea !== null && game.scorea !== undefined ? game.scorea.toString() : ''
    });
  };

  const formatDateTime = (date, time) => {
    if (!date) return '-';
    const dateStr = new Date(date).toLocaleDateString('de-DE');
    return time ? `${dateStr} ${time}` : dateStr;
  };

  const getTeamName = (teamId) => {
    if (!teamId) return 'Unknown Team';
    const team = teams.find(t => t._id === teamId);
    return team ? team.name : 'Unknown Team';
  };
  if (loading) {
    return (
      <div className="">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
          <div className="modern-card p-8 text-center">
            <div className="loading loading-spinner loading-lg text-blue-500 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Loading games...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="">
        {/* Header */}
        <div className="mb-12 fade-in">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
            <button 
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => navigate('/schedule')}
            >
              📅 Schedule
            </button>
            <span>→</span>
            <button 
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => navigate(`/schedule/${projectId}`)}
            >
              🎯 {project?.name || 'Project'}
            </button>
            <span>→</span>
            <span className="text-gray-800 dark:text-white font-medium">
              🏆 {round?.name || 'Round'}
            </span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                {round?.name || 'Round'} 🏆
              </h1>
              {round?.start && (
                <div className="flex items-center gap-2 text-xl text-gray-600 dark:text-gray-300 mb-2">
                  <span>📅</span>
                  <span>{new Date(round.start).toLocaleDateString('de-DE')}</span>
                </div>
              )}
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Manage and track games for this round
              </p>
            </div>
            
            <div className="flex gap-4">
              <button
                className="btn-modern-primary"
                onClick={() => setShowModal(true)}
              >
                <span className="mr-2">➕</span>
                Add Game
              </button>
              <button
                className={`btn-modern-outline ${showQuickAdd ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                onClick={() => setShowQuickAdd(!showQuickAdd)}
              >
                <span className="mr-2">⚡</span>
                Quick Add
              </button>
          </div>
        </div>
      </div>      {error && (
        <div className="alert alert-error mb-6">
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success mb-6">
          <span>✅ {successMessage}</span>
        </div>
      )}      {/* Quick Add Game Interface */}
      {showQuickAdd && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg mb-6">
          {/* Desktop Version */}
          <div className="hidden lg:block p-6">
            <h3 className="text-lg font-semibold mb-4">Add Game - Available Teams</h3>
            
            {/* Available Teams (not yet used in this round) */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
              {teams.filter(team => {
                // Filter out teams that are already playing in this round
                const isUsed = games.some(game => 
                  game.hid?._id === team._id || game.aid?._id === team._id
                );
                return !isUsed;
              }).map((team) => (                <div
                  key={team._id}
                  className={`btn btn-sm cursor-move transition-all duration-200 ${
                    selectedHomeTeam?._id === team._id ? 'btn-primary shadow-lg' : 
                  selectedAwayTeam?._id === team._id ? 'btn-secondary shadow-lg' : 'btn-outline hover:btn-ghost'
                }`}
                draggable="true"
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', JSON.stringify(team));
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onClick={() => {
                  if (selectedHomeTeam?._id === team._id) {
                    setSelectedHomeTeam(null);
                  } else if (selectedAwayTeam?._id === team._id) {
                    setSelectedAwayTeam(null);
                  } else if (!selectedHomeTeam) {
                    setSelectedHomeTeam(team);
                  } else if (!selectedAwayTeam) {
                    setSelectedAwayTeam(team);
                  }
                }}
                style={{ userSelect: 'none' }}
              >
                🤏 {team.name}
              </div>
            ))}
          </div>

          {/* Remaining Teams Count */}
          <div className="text-sm text-gray-600 mb-4">
            Available teams: {teams.filter(team => {
              const isUsed = games.some(game => 
                game.hid?._id === team._id || game.aid?._id === team._id
              );
              return !isUsed;
            }).length} | Games in round: {games.length}
          </div>

          {/* Drop Zones for Home and Away Teams */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">HOME TEAM</div>
              <div 
                className={`border-2 border-dashed rounded-lg p-4 min-h-[80px] min-w-[180px] flex items-center justify-center transition-colors ${
                  selectedHomeTeam ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-100');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
                  try {
                    const team = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (selectedAwayTeam?._id !== team._id) {
                      setSelectedHomeTeam(team);
                    }
                  } catch (err) {
                    console.error('Error dropping team:', err);
                  }
                }}
              >
                {selectedHomeTeam ? (
                  <div className="text-center">
                    <div className="font-semibold text-blue-700">{selectedHomeTeam.name}</div>
                    <button 
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={() => setSelectedHomeTeam(null)}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-gray-400">Drop Home Team Here</div>
                    <div className="text-xs text-gray-300">or click team above</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-3xl font-bold text-gray-400 mx-4">VS</div>
            
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">AWAY TEAM</div>
              <div 
                className={`border-2 border-dashed rounded-lg p-4 min-h-[80px] min-w-[180px] flex items-center justify-center transition-colors ${
                  selectedAwayTeam ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-green-500', 'bg-green-100');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-green-500', 'bg-green-100');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-green-500', 'bg-green-100');
                  try {
                    const team = JSON.parse(e.dataTransfer.getData('text/plain'));
                    if (selectedHomeTeam?._id !== team._id) {
                      setSelectedAwayTeam(team);
                    }
                  } catch (err) {
                    console.error('Error dropping team:', err);
                  }
                }}
              >
                {selectedAwayTeam ? (
                  <div className="text-center">
                    <div className="font-semibold text-green-700">{selectedAwayTeam.name}</div>
                    <button 
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={() => setSelectedAwayTeam(null)}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-gray-400">Drop Away Team Here</div>
                    <div className="text-xs text-gray-300">or click team above</div>
                  </div>
                )}
              </div>
            </div>
          </div>          {/* Create Game Button */}          <div className="text-center">
            <button
              className="btn btn-success"
              disabled={!selectedHomeTeam || !selectedAwayTeam}
              onClick={handleQuickCreateGame}
            >
              Create Game ({selectedHomeTeam?.name || '?'} vs {selectedAwayTeam?.name || '?'})
            </button>
          </div>
          </div>

          {/* Mobile Version */}
          <div className="lg:hidden p-4">
            <h3 className="text-lg font-semibold mb-4 text-center">🆕 Add New Game</h3>
            
            {/* Mobile Team Selection */}
            <div className="space-y-4">
              {/* Home Team Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Home Team</label>
                <select
                  value={selectedHomeTeam?._id || ''}
                  onChange={(e) => {
                    const team = teams.find(t => t._id === e.target.value);
                    setSelectedHomeTeam(team || null);
                  }}
                  className="select select-bordered w-full"
                >
                  <option value="">Select Home Team</option>
                  {teams.filter(team => {
                    const isUsed = games.some(game => 
                      game.hid?._id === team._id || game.aid?._id === team._id
                    );
                    return !isUsed && team._id !== selectedAwayTeam?._id;
                  }).map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Away Team Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Away Team</label>
                <select
                  value={selectedAwayTeam?._id || ''}
                  onChange={(e) => {
                    const team = teams.find(t => t._id === e.target.value);
                    setSelectedAwayTeam(team || null);
                  }}
                  className="select select-bordered w-full"
                >
                  <option value="">Select Away Team</option>
                  {teams.filter(team => {
                    const isUsed = games.some(game => 
                      game.hid?._id === team._id || game.aid?._id === team._id
                    );
                    return !isUsed && team._id !== selectedHomeTeam?._id;
                  }).map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Game Preview */}
              {selectedHomeTeam && selectedAwayTeam && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-center">
                    <div className="text-sm text-gray-500 mb-2">MATCHUP PREVIEW</div>
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <div className="font-semibold text-blue-600">{selectedHomeTeam.name}</div>
                        <div className="text-xs text-gray-500">HOME</div>
                      </div>
                      <div className="mx-4 text-gray-400">VS</div>
                      <div className="text-center">
                        <div className="font-semibold text-purple-600">{selectedAwayTeam.name}</div>
                        <div className="text-xs text-gray-500">AWAY</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Available Teams Counter */}
              <div className="text-center text-sm text-gray-600">
                Available teams: {teams.filter(team => {
                  const isUsed = games.some(game => 
                    game.hid?._id === team._id || game.aid?._id === team._id
                  );
                  return !isUsed;
                }).length} | Games in round: {games.length}
              </div>

              {/* Create Button */}
              <button
                className="btn btn-success w-full btn-lg"
                disabled={!selectedHomeTeam || !selectedAwayTeam}
                onClick={handleQuickCreateGame}
              >
                ✅ Create Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Games List */}
      <div className="space-y-3">
        {games.map((game) => (
          <div key={game._id} className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
            {/* Desktop Layout */}
            <div className="hidden lg:block p-4">
              <div className="flex items-center justify-between">
                {/* Game Info */}
                <div className="flex items-center gap-8 flex-1">                  {/* Date */}
                  <div className="text-center min-w-[100px]">
                    {inlineEditGame === game._id ? (
                      <div className="space-y-1">
                        <input
                          type="date"
                          value={inlineEditData.startdate}
                          onChange={(e) => setInlineEditData({...inlineEditData, startdate: e.target.value})}
                          className="input input-xs w-full text-xs"
                        />
                        <input
                          type="time"
                          value={inlineEditData.starttime}
                          onChange={(e) => setInlineEditData({...inlineEditData, starttime: e.target.value})}
                          className="input input-xs w-full text-xs"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-gray-600 font-medium">
                          {game.startdate ? new Date(game.startdate).toLocaleDateString('de-DE') : '-'}
                        </div>
                        {game.starttime && (
                          <div className="text-xs text-gray-500">{game.starttime}</div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Teams and Score - Layout like in screenshot */}
                  <div className="flex items-center gap-4 flex-1 justify-center">
                    <div className="text-right min-w-[150px]">
                      <div className="font-semibold text-gray-800">
                        {game.hid?.name || 'Home Team'}
                      </div>
                    </div>
                      <div className="flex items-center gap-2">
                      {inlineEditGame === game._id ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              value={inlineEditData.scoreh}
                              onChange={(e) => setInlineEditData({...inlineEditData, scoreh: e.target.value})}
                              className="input input-sm w-16 text-center"
                            />
                            <span className="text-lg font-bold">:</span>
                            <input
                              type="number"
                              min="0"
                              value={inlineEditData.scorea}
                              onChange={(e) => setInlineEditData({...inlineEditData, scorea: e.target.value})}
                              className="input input-sm w-16 text-center"
                          />
                        </div>                        {/* Auto-play indicator */}
                        {(inlineEditData.scoreh !== '' || inlineEditData.scorea !== '') && (
                          <div className="text-xs text-green-600 font-medium">
                            ✓ Will mark as played
                          </div>
                        )}
                      </div>
                    ) : (                      <div 
                        className={`text-white px-3 py-1 rounded font-bold text-lg min-w-[60px] text-center cursor-pointer transition-colors ${
                          game.played ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        onClick={() => handleInlineEdit(game)}
                        title={game.played ? "Game played - Click to edit" : "Game scheduled - Click to edit score"}
                      >                        {game.played ? (
                          <>
                            {game.scoreh ?? 0} : {game.scorea ?? 0}
                            <div className="text-xs opacity-75">FINAL</div>
                          </>
                        ) : (
                          '- : -'
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-left min-w-[150px]">
                    <div className="font-semibold text-gray-800">
                      {game.aid?.name || 'Away Team'}
                    </div>
                  </div>
                </div>
              </div>              {/* Actions */}
              <div className="flex gap-1.5 items-center flex-shrink-0">
                {inlineEditGame === game._id ? (
                  <>
                    <button className="btn btn-sm btn-success" onClick={() => handleInlineSave(game._id)}>
                      ✓ Save
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => { setInlineEditGame(null); setInlineEditData({}); }}>
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-xs btn-outline" onClick={() => handleEdit(game)} title="Edit game details">
                      ✏️ Edit
                    </button>
                    <button className="btn btn-xs btn-outline" onClick={() => setRosterGame(game)} title="Lineup / Roster">
                      👥 Lineup
                    </button>
                    <button className="btn btn-xs btn-outline" onClick={() => setEventsGame(game)} title="Game Events">
                      ⚽ Events
                    </button>
                    <button className="btn btn-xs btn-outline" onClick={() => navigate(`/schedule/${projectId}/rounds/${roundId}/games/${game._id}/ticker`)} title="Live Ticker">
                      📡 Ticker
                    </button>
                    <button className="btn btn-xs btn-error btn-square" onClick={() => handleDelete(game._id)} title="Delete game">
                      🗑️
                    </button>
                  </>
                )}
              </div>
            </div>
            {/* Referee / Stadium info row */}
            {(game.refid?.name || game.stid?.name) && (
              <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex gap-6 text-xs text-gray-500">
                {game.refid?.name && <span>🧑‍⚖️ {game.refid.name}</span>}
                {game.stid?.name && <span>🏟️ {game.stid.name}</span>}
              </div>
            )}
            </div>

            {/* Mobile Layout */}
            <div className="lg:hidden p-4 space-y-4">
              {/* Game Header - Date and Status */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  {inlineEditGame === game._id ? (
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={inlineEditData.startdate}
                        onChange={(e) => setInlineEditData({...inlineEditData, startdate: e.target.value})}
                        className="input input-sm text-sm"
                      />
                      <input
                        type="time"
                        value={inlineEditData.starttime}
                        onChange={(e) => setInlineEditData({...inlineEditData, starttime: e.target.value})}
                        className="input input-sm text-sm"
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {game.startdate ? new Date(game.startdate).toLocaleDateString('de-DE') : 'Date TBD'}
                      </div>
                      {game.starttime && (
                        <div className="text-xs text-gray-500">{game.starttime}</div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className={`badge ${game.played ? 'badge-success' : 'badge-warning'}`}>
                  {game.played ? 'Played' : 'Scheduled'}
                </div>
              </div>

              {/* Teams and Score - Mobile Optimized */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-2">MATCHUP</div>
                  
                  {/* Home Team */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="text-left">
                      <div className="font-semibold text-gray-800">
                        {game.hid?.name || 'Home Team'}
                      </div>
                      <div className="text-xs text-gray-500">HOME</div>
                    </div>
                    
                    {inlineEditGame === game._id ? (
                      <input
                        type="number"
                        min="0"
                        value={inlineEditData.scoreh}
                        onChange={(e) => setInlineEditData({...inlineEditData, scoreh: e.target.value})}
                        className="input input-sm w-16 text-center text-lg font-bold"
                        placeholder="0"
                      />
                    ) : (
                      <div className="text-2xl font-bold text-blue-600">
                        {game.scoreh !== null && game.scoreh !== undefined ? game.scoreh : '-'}
                      </div>
                    )}
                  </div>

                  <div className="text-center py-2">
                    <span className="text-gray-400 text-sm">vs</span>
                  </div>

                  {/* Away Team */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div className="text-left">
                      <div className="font-semibold text-gray-800">
                        {game.aid?.name || 'Away Team'}
                      </div>
                      <div className="text-xs text-gray-500">AWAY</div>
                    </div>
                    
                    {inlineEditGame === game._id ? (
                      <input
                        type="number"
                        min="0"
                        value={inlineEditData.scorea}
                        onChange={(e) => setInlineEditData({...inlineEditData, scorea: e.target.value})}
                        className="input input-sm w-16 text-center text-lg font-bold"
                        placeholder="0"
                      />
                    ) : (
                      <div className="text-2xl font-bold text-purple-600">
                        {game.scorea !== null && game.scorea !== undefined ? game.scorea : '-'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Score Update for Mobile */}
                {inlineEditGame === game._id && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleInlineSave(game._id)}
                      className="btn btn-sm btn-success flex-1"
                    >
                      ✓ Save
                    </button>
                    <button
                      onClick={() => setInlineEditGame(null)}
                      className="btn btn-sm btn-error flex-1"
                    >
                      ✗ Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Game Details - Mobile */}
              {(game.refid?.name || game.stid?.name) && (
                <div className="space-y-2 pt-3 border-t border-gray-200">
                  {game.refid?.name && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">🧑‍⚖️</span>
                      <span className="text-sm">{game.refid.name}</span>
                    </div>
                  )}
                  {game.stid?.name && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">🏟️</span>
                      <span className="text-sm">{game.stid.name}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile Action Buttons */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-200">
                {inlineEditGame !== game._id ? (
                  <>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => startInlineEdit(game)}
                    >
                      📝 Quick Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleEdit(game)}
                    >
                      ⚙️ Full Edit
                    </button>
                  </>
                ) : null}
                
                <button 
                  className="btn btn-sm btn-ghost"
                  title="Roster"
                >
                  👥 Roster
                </button>
                <button 
                  className="btn btn-sm btn-ghost"
                  title="Events"
                >
                  ⚽ Events
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>      {games.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">⚽</div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Games Found</h3>
          <p className="text-gray-500">Add your first game to this round.</p>
        </div>
      )}      {/* Game sub-modals */}
      {eventsGame && <GameEvents game={eventsGame} onClose={() => setEventsGame(null)} />}
      {rosterGame && <GameRoster game={rosterGame} onClose={() => setRosterGame(null)} />}
      {tickerGame && <GameTicker game={tickerGame} onClose={() => setTickerGame(null)} />}

      {/* Add/Edit Game Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box w-11/12 max-w-2xl">
            <h3 className="font-bold text-lg mb-4">
              {editingGame ? 'Edit Game' : 'Add Game'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Home Team */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Home Team *</span>
                  </label>
                  <select
                    required
                    value={formData.hid}
                    onChange={(e) => setFormData({...formData, hid: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="">Select Home Team</option>
                    {teams.map((team) => (
                      <option key={team._id} value={team._id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Away Team */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Away Team *</span>
                  </label>
                  <select
                    required
                    value={formData.aid}
                    onChange={(e) => setFormData({...formData, aid: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="">Select Away Team</option>
                    {teams.map((team) => (
                      <option key={team._id} value={team._id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Date</span>
                  </label>
                  <input
                    type="date"
                    value={formData.startdate}
                    onChange={(e) => setFormData({...formData, startdate: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Time */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Time</span>
                  </label>
                  <input
                    type="time"
                    value={formData.starttime}
                    onChange={(e) => setFormData({...formData, starttime: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Referee */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Referee</span>
                  </label>
                  <select
                    value={formData.refid}
                    onChange={(e) => setFormData({...formData, refid: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="">Select Referee</option>
                    {referees.map((referee) => (
                      <option key={referee._id} value={referee._id}>
                        {referee.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Stadium */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Stadium</span>
                  </label>
                  <select
                    value={formData.stid}
                    onChange={(e) => setFormData({...formData, stid: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="">Select Stadium</option>
                    {stadia.map((stadium) => (
                      <option key={stadium._id} value={stadium._id}>
                        {stadium.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Home Score */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Home Score</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.scoreh}
                    onChange={(e) => setFormData({...formData, scoreh: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Away Score */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Away Score</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.scorea}
                    onChange={(e) => setFormData({...formData, scorea: e.target.value})}
                    className="input input-bordered"
                  />
                </div>
              </div>

              {/* Played */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Game Played</span>
                  <input
                    type="checkbox"
                    checked={formData.played}
                    onChange={(e) => setFormData({...formData, played: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>

              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingGame ? 'Update' : 'Create'} Game
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
      </div>
    </div>
  );
};

export default RoundGames;
